import { taskEditorApi } from "api/taskEditorApi";
import {
  normalizeTaskEditorState,
  type PublishedTaskEditorTask,
} from "pages/task-editor/utils/taskEditorRuntime";
import type { TaskEditorState } from "pages/task-editor/utils/taskEditorUtils";
import { useCallback, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import {
  taskEditorDraftsByTaskIdAtom,
  taskEditorPublishedTasksAtom,
  taskEditorStoreHydratedAtom,
  taskEditorStoreLoadingAtom,
  taskEditorWorkingDraftAtom,
} from "recoil/taskEditorStoreAtom";

function sortPublishedTasks(tasks: PublishedTaskEditorTask[]) {
  return [...tasks].sort((left, right) =>
    right.updatedAt.localeCompare(left.updatedAt)
  );
}

function upsertPublishedTask(
  tasks: PublishedTaskEditorTask[],
  nextTask: PublishedTaskEditorTask,
  currentTaskId?: string
) {
  const normalizedCurrentTaskId = currentTaskId?.trim();
  const otherTasks = tasks
    .filter(
      (task) =>
        task.task_id !== nextTask.task_id &&
        task.task_id !== normalizedCurrentTaskId
    )
    .map((task) => {
      if (
        nextTask.is_key_activity === true &&
        nextTask.set_number !== undefined &&
        task.set_number === nextTask.set_number &&
        task.is_key_activity === true
      ) {
        return {
          ...task,
          is_key_activity: false,
        };
      }

      return task;
    });

  return sortPublishedTasks([nextTask, ...otherTasks]);
}

function createFallbackPublishedTask(
  state: TaskEditorState
): PublishedTaskEditorTask {
  const normalizedState = normalizeTaskEditorState(state);
  const now = new Date().toISOString();

  return {
    ...normalizedState,
    task_id: normalizedState.task_id || "NEW-TASK-ID",
    title: normalizedState.title || "새 Task 제목",
    versionId: now,
    updatedAt: now,
  };
}

function getTaskEditorStateSignature(state: TaskEditorState) {
  return JSON.stringify(normalizeTaskEditorState(state));
}

export default function useTaskEditorStore() {
  const [publishedTasks, setPublishedTasks] = useRecoilState(
    taskEditorPublishedTasksAtom
  );
  const [workingDraft, setWorkingDraft] = useRecoilState(
    taskEditorWorkingDraftAtom
  );
  const [taskDraftsByTaskId, setTaskDraftsByTaskId] = useRecoilState(
    taskEditorDraftsByTaskIdAtom
  );
  const [isStoreHydrated, setIsStoreHydrated] = useRecoilState(
    taskEditorStoreHydratedAtom
  );
  const [isStoreLoading, setIsStoreLoading] = useRecoilState(
    taskEditorStoreLoadingAtom
  );
  const latestWorkingDraftRef = useRef<TaskEditorState | undefined>(undefined);
  const latestTaskDraftsByTaskIdRef = useRef<Record<string, TaskEditorState>>(
    {}
  );
  const saveDraftTimerRef = useRef<number | undefined>(undefined);
  const saveTaskDraftTimersRef = useRef<Record<string, number>>({});
  // 키 입력당 무거운 normalize/stringify 를 피하기 위해, 호출 시에는 ref 에만 저장.
  // 500ms debounce 안에서만 실제 normalize → diff check → atom update → API call.
  const pendingTaskDraftStateRef = useRef<
    Record<string, { task_id: string; state: TaskEditorState }>
  >({});

  useEffect(() => {
    latestTaskDraftsByTaskIdRef.current = taskDraftsByTaskId;
  }, [taskDraftsByTaskId]);

  useEffect(() => {
    return () => {
      if (saveDraftTimerRef.current !== undefined) {
        window.clearTimeout(saveDraftTimerRef.current);
        saveDraftTimerRef.current = undefined;
      }

      const nextDraft = latestWorkingDraftRef.current;
      // 서버가 working draft 저장 시 task_id 를 요구 — 비어있으면 호출 자체를 스킵.
      if (nextDraft && nextDraft.task_id?.trim()) {
        taskEditorApi.saveWorkingDraft(nextDraft).catch(() => {});
      }

      const taskDraftTimers = saveTaskDraftTimersRef.current;
      Object.entries(taskDraftTimers).forEach(([task_id, timerId]) => {
        window.clearTimeout(timerId);
        delete taskDraftTimers[task_id];

        // unmount 시점에는 pending 에 최신 키 입력이 있을 수 있음 — 우선 flush.
        const pending = pendingTaskDraftStateRef.current[task_id];
        if (pending) {
          delete pendingTaskDraftStateRef.current[task_id];
          const normalizedState = normalizeTaskEditorState({
            ...pending.state,
            task_id: pending.task_id,
          });
          taskEditorApi
            .saveTaskDraft(pending.task_id, normalizedState)
            .catch(() => {});
          return;
        }

        const nextTaskDraft = latestTaskDraftsByTaskIdRef.current[task_id];
        if (!nextTaskDraft) {
          return;
        }

        taskEditorApi.saveTaskDraft(task_id, nextTaskDraft).catch(() => {});
      });
    };
  }, []);

  const hydratePublishedTasks = useCallback(async () => {
    setIsStoreLoading(true);
    try {
      const nextTasks = await taskEditorApi.getPublishedTasks();
      setPublishedTasks(nextTasks);
      setIsStoreHydrated(true);
      return nextTasks;
    } finally {
      setIsStoreLoading(false);
    }
  }, [setIsStoreHydrated, setIsStoreLoading, setPublishedTasks]);

  const hydrateWorkingDraft = useCallback(async () => {
    const nextDraft: TaskEditorState | undefined =
      await taskEditorApi.getWorkingDraft();
    setWorkingDraft(nextDraft);
    latestWorkingDraftRef.current = nextDraft;
    return nextDraft;
  }, [setWorkingDraft]);

  const hydrateTaskDrafts = useCallback(async () => {
    const nextTaskDraftsByTaskId = await taskEditorApi.getTaskDraftsByTaskId();
    setTaskDraftsByTaskId(nextTaskDraftsByTaskId);
    latestTaskDraftsByTaskIdRef.current = nextTaskDraftsByTaskId;
    return nextTaskDraftsByTaskId;
  }, [setTaskDraftsByTaskId]);

  const hydrateTaskEditorStore = useCallback(async () => {
    setIsStoreLoading(true);
    try {
      const [nextTasks, nextDraft, nextTaskDraftsByTaskId]: [
        PublishedTaskEditorTask[],
        TaskEditorState | undefined,
        Record<string, TaskEditorState>,
      ] = await Promise.all([
        taskEditorApi.getPublishedTasks(),
        taskEditorApi.getWorkingDraft(),
        taskEditorApi.getTaskDraftsByTaskId(),
      ]);
      setPublishedTasks(nextTasks);
      setWorkingDraft(nextDraft);
      setTaskDraftsByTaskId(nextTaskDraftsByTaskId);
      latestWorkingDraftRef.current = nextDraft;
      latestTaskDraftsByTaskIdRef.current = nextTaskDraftsByTaskId;
      setIsStoreHydrated(true);
      return {
        publishedTasks: nextTasks,
        workingDraft: nextDraft,
        taskDraftsByTaskId: nextTaskDraftsByTaskId,
      };
    } finally {
      setIsStoreLoading(false);
    }
  }, [
    setIsStoreHydrated,
    setIsStoreLoading,
    setPublishedTasks,
    setTaskDraftsByTaskId,
    setWorkingDraft,
  ]);

  const saveWorkingDraft = useCallback(
    async (state: TaskEditorState) => {
      const normalizedState = normalizeTaskEditorState(state);
      setWorkingDraft(normalizedState);
      latestWorkingDraftRef.current = normalizedState;

      if (saveDraftTimerRef.current !== undefined) {
        window.clearTimeout(saveDraftTimerRef.current);
      }

      saveDraftTimerRef.current = window.setTimeout(() => {
        const nextDraft = latestWorkingDraftRef.current;
        saveDraftTimerRef.current = undefined;
        if (!nextDraft || !nextDraft.task_id?.trim()) {
          // 서버가 task_id 를 요구 — 빈 working draft 는 저장 호출 자체를 스킵.
          return;
        }
        taskEditorApi.saveWorkingDraft(nextDraft).catch(() => {});
      }, 500);
    },
    [setWorkingDraft]
  );

  const publishTask = useCallback(
    async (state: TaskEditorState, currentTaskId?: string) => {
      const normalizedState = normalizeTaskEditorState(state);
      const savedTask =
        (await taskEditorApi.savePublishedTask(
          normalizedState,
          currentTaskId
        )) ?? createFallbackPublishedTask(normalizedState);

      setPublishedTasks((currentTasks) =>
        upsertPublishedTask(currentTasks, savedTask, currentTaskId)
      );

      return savedTask;
    },
    [setPublishedTasks]
  );

  const removeTask = useCallback(
    async (task_id: string) => {
      const normalizedTaskId = task_id.trim();
      await taskEditorApi.removePublishedTask(normalizedTaskId);
      setPublishedTasks((currentTasks) =>
        currentTasks.filter((task) => task.task_id !== normalizedTaskId)
      );
    },
    [setPublishedTasks]
  );

  const saveTaskDraft = useCallback(
    async (task_id: string, state: TaskEditorState) => {
      const normalizedTaskId = task_id.trim();
      if (!normalizedTaskId) {
        return;
      }

      // 키 입력당 호출되는 핫 경로 — ref 에만 저장하고 debounce 안에서 무거운 일.
      pendingTaskDraftStateRef.current[normalizedTaskId] = {
        task_id: normalizedTaskId,
        state,
      };

      const taskDraftTimers = saveTaskDraftTimersRef.current;
      if (taskDraftTimers[normalizedTaskId] !== undefined) {
        window.clearTimeout(taskDraftTimers[normalizedTaskId]);
      }

      taskDraftTimers[normalizedTaskId] = window.setTimeout(() => {
        delete taskDraftTimers[normalizedTaskId];
        const pending = pendingTaskDraftStateRef.current[normalizedTaskId];
        if (!pending) return;
        delete pendingTaskDraftStateRef.current[normalizedTaskId];

        // 여기서만 deep clone + diff check + atom update.
        const normalizedState = normalizeTaskEditorState({
          ...pending.state,
          task_id: pending.task_id,
        });
        const currentTaskDraft =
          latestTaskDraftsByTaskIdRef.current[pending.task_id];
        if (
          currentTaskDraft &&
          getTaskEditorStateSignature(currentTaskDraft) ===
            getTaskEditorStateSignature(normalizedState)
        ) {
          return;
        }

        setTaskDraftsByTaskId((currentDraftsByTaskId) => {
          const nextDraftsByTaskId = {
            ...currentDraftsByTaskId,
            [pending.task_id]: normalizedState,
          };
          latestTaskDraftsByTaskIdRef.current = nextDraftsByTaskId;
          return nextDraftsByTaskId;
        });

        taskEditorApi
          .saveTaskDraft(pending.task_id, normalizedState)
          .catch(() => {});
      }, 500);
    },
    [setTaskDraftsByTaskId]
  );

  const removeTaskDraft = useCallback(
    async (task_id: string) => {
      const normalizedTaskId = task_id.trim();
      if (!normalizedTaskId) {
        return;
      }

      const taskDraftTimers = saveTaskDraftTimersRef.current;
      if (taskDraftTimers[normalizedTaskId] !== undefined) {
        window.clearTimeout(taskDraftTimers[normalizedTaskId]);
        delete taskDraftTimers[normalizedTaskId];
      }
      delete pendingTaskDraftStateRef.current[normalizedTaskId];

      setTaskDraftsByTaskId((currentDraftsByTaskId) => {
        if (!(normalizedTaskId in currentDraftsByTaskId)) {
          return currentDraftsByTaskId;
        }

        const nextDraftsByTaskId = { ...currentDraftsByTaskId };
        delete nextDraftsByTaskId[normalizedTaskId];
        latestTaskDraftsByTaskIdRef.current = nextDraftsByTaskId;
        return nextDraftsByTaskId;
      });

      await taskEditorApi.removeTaskDraft(normalizedTaskId);
    },
    [setTaskDraftsByTaskId]
  );

  return {
    publishedTasks,
    workingDraft,
    taskDraftsByTaskId,
    isStoreHydrated,
    isStoreLoading,
    hydratePublishedTasks,
    hydrateWorkingDraft,
    hydrateTaskDrafts,
    hydrateTaskEditorStore,
    saveWorkingDraft,
    saveTaskDraft,
    publishTask,
    removeTaskDraft,
    removeTask,
  };
}
